import React from 'react';
import { HiArrowRight } from 'react-icons/hi';

/**
 * The shared vocabulary of the desktop landing page, lifted out of the hero so
 * every section below it is built from the same parts:
 *
 *   - panels: big rounded surfaces sitting in the same side gutter as the
 *     hero's panels, so their edges line up all the way down the page;
 *   - three surfaces: brand red (rgb(var(--brand))) with white type, a warm off-white
 *     with a faint dot grid, and plain white cards;
 *   - red-tinted shadows rather than grey ones;
 *   - pill buttons with an arrow, and a tracked-caps eyebrow over each title.
 *
 * Everything here is desktop styling. The home page renders a separate mobile
 * tree (lg:hidden), so none of this reaches phones until the mobile pass.
 */

export const BRAND_RED = 'rgb(var(--brand))';
export const SURFACE = '#F6F3F0';

// Same gutter as the hero's panels (see Hero.js / HeroShowcase).
export const GUTTER = 'px-[clamp(0.75rem,1.6vw,2rem)]';
// Vertical rhythm between stacked panels.
export const PANEL_GAP = 'pt-[clamp(1rem,2vw,1.75rem)]';
// Inner padding of a full-width panel.
export const PANEL_PAD = 'px-[clamp(2rem,4.5vw,5rem)] py-[clamp(2.75rem,5.5vw,5.5rem)]';

export const RED_SHADOW = 'shadow-[0_30px_60px_-36px_rgb(var(--brand-shadow)/0.7)]';
export const CARD_SHADOW = 'shadow-[0_18px_40px_-28px_rgba(17,24,39,0.45)]';

export const DOT_GRID_STYLE = {
  backgroundImage: 'radial-gradient(rgba(17,24,39,0.07) 1px, transparent 1px)',
  backgroundSize: '18px 18px',
};

const WHITE_GRAIN_STYLE = {
  backgroundImage: 'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)',
  backgroundSize: '20px 20px',
};

/** Outlined pharmacy cross — the recurring mark on red surfaces. */
export const PharmacyCross = ({ className = '', style }) => (
  <svg className={className} style={style} viewBox="0 0 100 100" fill="none" aria-hidden="true">
    <path
      d="M36 6h28a4 4 0 0 1 4 4v22h22a4 4 0 0 1 4 4v28a4 4 0 0 1-4 4H68v22a4 4 0 0 1-4 4H36a4 4 0 0 1-4-4V68H10a4 4 0 0 1-4-4V36a4 4 0 0 1 4-4h22V10a4 4 0 0 1 4-4z"
      stroke="currentColor"
      strokeWidth="2.5"
    />
  </svg>
);

/**
 * Decoration for a red surface: white dot grain, a light from the top right,
 * a deeper foot, and optionally the cropped cross. Clipped to the parent's
 * radius; place it first inside a `relative` red box.
 */
export const RedSurfaceDecor = ({ cross = true, radius = 'rounded-[2.5rem]', crossClassName }) => (
  <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${radius}`}>
    <div className="absolute inset-0" style={WHITE_GRAIN_STYLE} />
    <div
      className="absolute inset-0"
      style={{
        background:
          'radial-gradient(120% 90% at 100% 0%, rgba(255,255,255,0.16), transparent 55%), linear-gradient(180deg, transparent 40%, rgb(var(--brand-shadow)/0.28) 100%)',
      }}
    />
    {cross && (
      <PharmacyCross
        className={crossClassName || 'absolute -bottom-[16%] -right-[5%] h-[75%] w-auto rotate-[-10deg] text-white/[0.09]'}
      />
    )}
  </div>
);

/** Dot grid for an off-white surface. */
export const DotGrid = ({ radius = 'rounded-[2.5rem]', maskImage }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute inset-0 ${radius}`}
    style={{ ...DOT_GRID_STYLE, ...(maskImage ? { WebkitMaskImage: maskImage, maskImage } : null) }}
  />
);

/** Tracked-caps label with a dot. `tone` = 'light' (on white) | 'onRed'. */
export const Eyebrow = ({ children, tone = 'light', pulse = false }) => (
  <span
    className={`inline-flex items-center gap-2 rounded-full py-1 pl-2.5 pr-3 text-[11px] font-semibold uppercase tracking-[0.2em] ${
      tone === 'onRed'
        ? 'bg-white/15 text-white ring-1 ring-inset ring-white/20 backdrop-blur-sm'
        : 'bg-white text-gray-500 ring-1 ring-inset ring-black/[0.07]'
    }`}
  >
    <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
      {pulse && (
        <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${tone === 'onRed' ? 'bg-white' : 'bg-brand landing:bg-petal'}`} />
      )}
      <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${tone === 'onRed' ? 'bg-white' : 'bg-brand landing:bg-petal'}`} />
    </span>
    {children}
  </span>
);

/**
 * Title with a highlighted phrase. The highlight is red on light surfaces and
 * stays white (the whole title goes white) on red ones.
 */
export const SectionTitle = ({ as: Tag = 'h2', lead, highlight, tail, tone = 'light', className = '' }) => (
  <Tag
    className={`text-[clamp(2rem,3.4vw,3.5rem)] font-extrabold leading-[1.02] tracking-[-0.04em] ${
      tone === 'onRed' ? 'text-white' : 'text-gray-900'
    } ${className}`}
  >
    {lead}
    {lead && highlight ? ' ' : null}
    {highlight && <span className={tone === 'onRed' ? 'text-white' : 'text-brand'}>{highlight}</span>}
    {tail}
  </Tag>
);

/**
 * Pill button with a trailing arrow.
 *   solid   — red fill (on light surfaces)
 *   white   — white fill, red text (on red surfaces)
 *   outline — white with a hairline (secondary, on light surfaces)
 *   glass   — translucent white (secondary, on red surfaces)
 */
export const PillButton = ({ variant = 'solid', onClick, href, children, className = '', arrow = true, ...rest }) => {
  const styles = {
    solid: 'bg-brand text-white shadow-[0_12px_28px_-12px_rgb(var(--brand)/0.55)] hover:bg-brand-hover focus-visible:ring-red-300 focus-visible:ring-offset-white',
    white: 'bg-white text-brand shadow-[0_12px_26px_-14px_rgb(var(--brand-shadow-deep)/0.8)] hover:bg-red-50 focus-visible:ring-white/70 focus-visible:ring-offset-brand',
    outline: 'bg-white text-gray-800 ring-1 ring-inset ring-black/[0.08] hover:text-brand hover:ring-red-200 focus-visible:ring-red-300 focus-visible:ring-offset-white',
    glass: 'bg-white/15 text-white ring-1 ring-inset ring-white/25 hover:bg-white/25 focus-visible:ring-white/70 focus-visible:ring-offset-brand',
  }[variant];
  const cls = `group/pill inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${styles} ${className}`;
  const inner = (
    <>
      {children}
      {arrow && <HiArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/pill:translate-x-0.5" aria-hidden="true" />}
    </>
  );
  return href ? (
    <a href={href} className={cls} {...rest}>{inner}</a>
  ) : (
    <button type="button" onClick={onClick} className={cls} {...rest}>{inner}</button>
  );
};

/** Outlined two-digit numeral, the same device as the bestseller ranks. */
export const OutlinedNumeral = ({ n, tone = 'light', className = '' }) => (
  <span
    aria-hidden="true"
    className={`select-none font-black leading-none tracking-tighter text-transparent [--numeral:rgb(var(--brand))] landing:[--numeral:#E33B4C] ${className}`}
    style={{ WebkitTextStroke: `1.5px ${tone === 'onRed' ? 'rgba(255,255,255,0.7)' : 'var(--numeral)'}` }}
  >
    {String(n).padStart(2, '0')}
  </span>
);
